import { Fragment, useCallback, useMemo, useState, useEffect } from "react";
import {
  BasketKPLabels,
  TBasketData,
  TBasketDataPerformanceGroup,
  TBasketOptionalExtrasGroup,
  TBasketPackage,
} from "../BasketKP.types";
import ConfirmModal from "shared/ConfirmModal";
import axios from "axios";
import * as R from "ramda";
import { useMedia } from "hooks";
import OptionalExtraCount from "./OptionalExtraCount";

function BasketItem({
  labels,
  basketPackage,
  renderPrice,
  updateBasketData,
  domain,
  basketItemIndex,
  performanceGroupIndex,
  basketData,
  setIsLoading,
  canBeDeleted,
  onErrorChange,
}: {
  labels: BasketKPLabels;
  basketPackage: TBasketPackage;
  renderPrice: (price: number | undefined) => string;
  updateBasketData: React.Dispatch<React.SetStateAction<TBasketData | null>>;
  domain: string;
  basketItemIndex: number;
  performanceGroupIndex: number;
  basketData: TBasketData;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  canBeDeleted: boolean;
  onErrorChange: (id: number, hasError: boolean) => void;
}) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [activeOptionalExtraGroup, setActiveOptionalExtraGroup] =
    useState<TBasketOptionalExtrasGroup | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState<string | number | null>(
    null,
  );
  const [isConfirmOptExtraOpen, setIsConfirmOptExtraOpen] = useState<
    string | number | null
  >(null);
  const { isTablet } = useMedia();
  const [showCannotRemoveMessage, setShowCannotRemoveMessage] = useState(false);

  const optionalExtraGroupToRender = useMemo(
    () =>
      activeOptionalExtraGroup
        ? [activeOptionalExtraGroup]
        : basketPackage.OptionalExtrasGroups,
    [activeOptionalExtraGroup, basketPackage.OptionalExtrasGroups],
  );

  const updateBasketProduct = useCallback(
    (dataToUpdate: TBasketData) => {
      const products = dataToUpdate?.PerformanceGroups.flatMap(
        (item) => item.BasketPackagesAndExtras,
      );

      const dataToSend = products.map((product) => {
        return {
          ...product,
          OptionalExtras: product.OptionalExtrasGroups?.flatMap((opt) =>
            opt.OptionalExtras.flatMap((oo) => oo.Items),
          ),
        };
      });

      setIsLoading(true);
      axios
        .request({
          data: {
            Products: dataToSend,
          },
          method: "POST",
          url: domain + `/api/sitecore/SecutixCheckout/updatebasket`,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          withCredentials: true,
        })
        .then((res) => {
          if (res.data.BasketId) {
            updateBasketData(res.data ?? { ...dataToUpdate, ...res.data });

            const basketHeaderWrapper =
              document.getElementsByClassName("icon-basket");
            if (basketHeaderWrapper && basketHeaderWrapper[0]) {
              if (!basketHeaderWrapper[0].classList.contains("active")) {
                basketHeaderWrapper[0].classList.add("active");
              }
            }

            document
              .querySelectorAll(".basket-li .basket-link-header")
              .forEach((elem) => elem.classList.add("active"));

            const basketHeaderElem = document.querySelectorAll(
              '[data-basketproperty="TotalItemsCount"]',
            );
            if (basketHeaderElem) {
              basketHeaderElem.forEach((elem) => {
                elem.textContent = res?.data?.TotalItems;
              });
            }
          }
        })
        .finally(() => setIsLoading(false));
    },
    [domain, setIsLoading, updateBasketData],
  );

  const handleDecreaseQuantity = useCallback(() => {
    if (basketPackage.BasketQuantity === 1) return;

    const group = basketData.PerformanceGroups[performanceGroupIndex];
    const newQuantity = basketPackage.BasketQuantity - 1;

    pushUpdateCartItem(basketPackage, group, newQuantity);

    updateBasketProduct(
      R.modifyPath(
        [
          "PerformanceGroups",
          performanceGroupIndex,
          "BasketPackagesAndExtras",
          basketItemIndex,
          "BasketQuantity",
        ],
        () => newQuantity,
        basketData,
      ),
    );
  }, [
    basketData,
    basketItemIndex,
    basketPackage,
    performanceGroupIndex,
    updateBasketProduct,
  ]);

  const handleIncreaseQuantity = useCallback(() => {
    const group = basketData.PerformanceGroups[performanceGroupIndex];
    const newQuantity = basketPackage.BasketQuantity + 1;

    pushUpdateCartItem(basketPackage, group, newQuantity);

    updateBasketProduct(
      R.modifyPath(
        [
          "PerformanceGroups",
          performanceGroupIndex,
          "BasketPackagesAndExtras",
          basketItemIndex,
          "BasketQuantity",
        ],
        () => newQuantity,
        basketData,
      ),
    );
  }, [
    basketData,
    basketItemIndex,
    basketPackage,
    performanceGroupIndex,
    updateBasketProduct,
  ]);

  const pushUpdateCartItem = (
    pkg: TBasketPackage,
    group: TBasketDataPerformanceGroup,
    newQuantity: number,
  ) => {
    const quantityDelta = newQuantity - pkg.BasketQuantity;
    if (quantityDelta === 0) return;

    const eventType = quantityDelta > 0 ? "add_to_cart" : "remove_from_cart";

    const item = {
      item_id: `${pkg.ProductId}-${pkg.AudienceSubCategoryId}`,
      item_name: pkg.ProductName,
      price: pkg.PriceWithoutVat,
      quantity: Math.abs(quantityDelta),
      item_category:
        group.SitecorePerformanceDescription.split("|").length > 1
          ? group.SitecorePerformanceDescription.split("|")[1].trim()
          : "Unknown Venue",
      item_category2: group.SitecorePerformance,
      item_category3:
        pkg.ProductName.indexOf(" - ") > 0
          ? pkg.ProductName.split(" - ")[0].trim()
          : pkg.ProductName,
      item_category5: pkg.Quantity.toString(),
    };

    console.log("dataLayer pushed for add/remove cart item:", {
      event: eventType,
      ...item,
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventType,
      ecommerce: {
        currency: "GBP",
        value: pkg.PriceWithoutVat * quantityDelta,
        items: [item],
      },
    });
  };

  const removeBasketProduct = useCallback(() => {
    const pkg =
      basketData.PerformanceGroups[performanceGroupIndex]
        .BasketPackagesAndExtras[basketItemIndex];
    const group = basketData.PerformanceGroups[performanceGroupIndex];

    pushRemoveFromCart(pkg, group); // push before data is wiped to 0
    updateBasketProduct(
      R.modifyPath(
        [
          "PerformanceGroups",
          performanceGroupIndex,
          "BasketPackagesAndExtras",
          basketItemIndex,
          "BasketQuantity",
        ],
        () => 0,
        basketData,
      ),
    );
  }, [basketData, basketItemIndex, performanceGroupIndex, updateBasketProduct]);

  const pushRemoveFromCart = (
    pkg: TBasketPackage,
    group: TBasketDataPerformanceGroup,
  ) => {
    const item = {
      item_id: `${pkg.ProductId}-${pkg.AudienceSubCategoryId}`,
      item_name: pkg.ProductName,
      price: pkg.PriceWithoutVat,
      quantity: pkg.BasketQuantity,
      item_category:
        group.SitecorePerformanceDescription.split("|").length > 1
          ? group.SitecorePerformanceDescription.split("|")[1].trim()
          : "Unknown Venue",
      item_category2: group.SitecorePerformance,
      item_category3:
        pkg.ProductName.indexOf(" - ") > 0
          ? pkg.ProductName.split(" - ")[0].trim()
          : pkg.ProductName,
      item_category5: pkg.Quantity.toString(),
    };

    console.log("dataLayer pushed for remove_from_cart:", item);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "remove_from_cart",
      ecommerce: {
        currency: "GBP",
        value: pkg.PriceWithoutVat * pkg.BasketQuantity,
        items: [item],
      },
    });
  };

  const removeOptExtraProduct = useCallback(() => {
    // @ts-ignore
    let newOptExtrasGroup = [];

    const optExtrasGroup = R.path(
      [
        "PerformanceGroups",
        performanceGroupIndex,
        "BasketPackagesAndExtras",
        basketItemIndex,
        "OptionalExtrasGroups",
      ],
      basketData,
    );

    optExtrasGroup?.forEach((group) => {
      const optExtraFoundIdx = group.OptionalExtras.findIndex(
        (it) => it.Items[0].ItemId === isConfirmOptExtraOpen,
      );

      newOptExtrasGroup.push(
        R.modifyPath(
          ["OptionalExtras", optExtraFoundIdx, "Items", 0, "BasketQuantity"],
          () => 0,
          group,
        ),
      );
    });

    updateBasketProduct(
      R.modifyPath(
        [
          "PerformanceGroups",
          performanceGroupIndex,
          "BasketPackagesAndExtras",
          basketItemIndex,
          "OptionalExtrasGroups",
        ],
        // @ts-expect-error
        () => newOptExtrasGroup,
        basketData,
      ),
    );
  }, [
    basketData,
    basketItemIndex,
    isConfirmOptExtraOpen,
    performanceGroupIndex,
    updateBasketProduct,
  ]);

  const optionalExtraMinPrice = useMemo(() => {
    const optExtras = basketPackage.OptionalExtrasGroups?.flatMap((opt) =>
      opt.OptionalExtras.flatMap((oo) => oo.Items),
    );

    // @ts-expect-error
    return Math.min(...optExtras?.map((item) => item.PriceWithVat));
  }, [basketPackage.OptionalExtrasGroups]);

  const optionalExtrasToShow = useMemo(() => {
    return basketPackage.OptionalExtrasGroups?.flatMap((opt) =>
      opt.OptionalExtras.flatMap((oo) => oo.Items),
    ).filter((opt) => opt.BasketQuantity > 0);
  }, [basketPackage.OptionalExtrasGroups]);

  const getErrorMessage = useMemo(() => {
    let errorMsg = "";
    if (!basketPackage.FullPrice && basketPackage?.PurchaseConstraint) {
      const quantity = basketPackage.BasketQuantity;
      const minQuantityOfRequiredASC =
        basketPackage?.PurchaseConstraint?.MinQuantityOfRequiredASC;
      const childRatio = basketPackage?.PurchaseConstraint?.ChildRatio;
      const parentThreshold =
        basketPackage?.PurchaseConstraint?.ParentThreshold;
      const actualChildRatio =
        childRatio && parentThreshold ? childRatio / parentThreshold : null;
      const audienceSubCategoryId =
        basketPackage?.PurchaseConstraint?.RequiredAudienceSubCategoryId;

      const basketPackagesAndExtras =
        basketData.PerformanceGroups[performanceGroupIndex]
          .BasketPackagesAndExtras;
      const parentElement = basketPackagesAndExtras.find(
        (el) => el.AudienceSubCategoryId === audienceSubCategoryId,
      );
      const adultCount = parentElement?.BasketQuantity || 0;

      if (minQuantityOfRequiredASC > adultCount && quantity > 0) {
        errorMsg = labels.AdultTariffThresholdText.replace(
          "{threshold}",
          minQuantityOfRequiredASC.toString(),
        );
      } else if (
        actualChildRatio &&
        adultCount > 0 &&
        quantity > 0 &&
        quantity / adultCount > actualChildRatio
      ) {
        errorMsg = labels.AdultTariffRatioText.replace(
          "{ratio}",
          actualChildRatio + "",
        );
      }
    }
    return errorMsg;
  }, [basketPackage.PurchaseConstraint]);

  const errorMessage = getErrorMessage;
  const hasError = Boolean(errorMessage);

  useEffect(() => {
    onErrorChange(basketItemIndex, hasError);
  }, [basketItemIndex, hasError, onErrorChange]);

  return (
    <>
      <div className="o-basket__package">
        <div className="o-basket__package__info">
          <span>{labels.PackageText}</span>
          <div>
            <span>{basketPackage.ProductName}</span>
            {!basketPackage.FullPrice && (
              <span
                style={{
                  paddingTop: "4px",
                  paddingRight: "8px",
                  paddingBottom: "4px",
                  paddingLeft: "8px",
                  borderRadius: "4px",
                  backgroundColor: "#2A4E5C",
                  fontSize: "14px",
                  marginLeft: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  color: "#FFFFFF",
                }}
              >
                {basketPackage.TariffName}
              </span>
            )}
          </div>
        </div>
        <div className="o-basket__divider"></div>
        <div className="o-basket__package__info mobile-per-row">
          <span>{labels.PricePerPersonText}</span>
          <span>
            {`${renderPrice(basketPackage.PriceWithoutVat / basketPackage.Quantity)} pp`}
          </span>
        </div>
        <div className="o-basket__package__quantity">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "14px" }}>Qty</span>
            <div className="o-basket__package__quantity-input">
              <button onClick={handleDecreaseQuantity}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.5"
                    y="0.5"
                    width="39"
                    height="39"
                    rx="19.5"
                    fill={
                      basketPackage.BasketQuantity === 1
                        ? "transparent"
                        : "#4FE8B5"
                    }
                  />
                  <rect
                    x="0.5"
                    y="0.5"
                    width="39"
                    height="39"
                    rx="19.5"
                    stroke={
                      basketPackage.BasketQuantity === 1 ? "#D7D7D7" : "#4FE8B5"
                    }
                  />
                  <path
                    d="M22.1758 20.3242H14V19.1758H22.1758V20.3242Z"
                    fill={
                      basketPackage.BasketQuantity === 1 ? "#D7D7D7" : "#242425"
                    }
                  />
                </svg>
              </button>
              <div>{basketPackage.BasketQuantity}</div>
              <button onClick={handleIncreaseQuantity}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.5"
                    y="0.5"
                    width="39"
                    height="39"
                    rx="19.5"
                    fill="#4FE8B5"
                  />
                  <rect
                    x="0.5"
                    y="0.5"
                    width="39"
                    height="39"
                    rx="19.5"
                    stroke="#4FE8B5"
                  />
                  <path
                    d="M24.9639 20.3223H20.7139V24.5723H19.2861V20.3223H15.0361V18.9277H19.2861V14.6777H20.7139V18.9277H24.9639V20.3223Z"
                    fill="#242425"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="o-basket__package__no-of-guests">
            <span>{labels.NoOfGuestText}</span>
            <span>{basketPackage.Quantity * basketPackage.BasketQuantity}</span>
          </div>
        </div>
        <div className="o-basket__package__info o-basket__package__info--mobile">
          <span>{labels.TotalText}</span>
          <span>
            {renderPrice(
              basketPackage.PriceWithoutVat * basketPackage.BasketQuantity,
            )}
          </span>
        </div>
        <div className="o-basket__divider hide-mobile"></div>
        <div
          className="o-basket__package__remove-btn"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <button
            onClick={() =>
              canBeDeleted
                ? setIsConfirmOpen(basketPackage.ProductId)
                : setShowCannotRemoveMessage(true)
            }
            className="a-button remove-btn"
            title={labels.RemoveButtonText}
            aria-label={labels.RemoveButtonText}
          >
            <i className="zmdi zmdi-delete" style={{ fontSize: "24px" }}></i>
          </button>
        </div>
        {errorMessage && (
          <div className="o-basket__package__error">{errorMessage}</div>
        )}
        {showCannotRemoveMessage && (
          <div className="o-basket__package__error">
            {labels.AdultTariffCannotBeRemovedText}
          </div>
        )}
      </div>
      {optionalExtrasToShow?.map((opt, idx) => (
        <div
          className="o-basket__package"
          key={idx}
          style={{ gridTemplateColumns: "2.7fr 1px 1.7fr 3fr 1fr 1px 60px" }}
        >
          <div className="o-basket__package__info">
            <span>Optional extra</span>
            <span>{opt.ItemName}</span>
          </div>
          <div className="o-basket__divider"></div>
          <div className="o-basket__package__info mobile-per-row">
            <span>Price per item</span>
            <span>{renderPrice(opt.PriceWithVat)}</span>
          </div>
          <div className="o-basket__package__quantity">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px" }}>Qty</span>
              <div className="o-basket__package__quantity-input">
                <button
                  onClick={() => {
                    // @ts-ignore
                    let newOptExtrasGroup = [];

                    const optExtrasGroup = R.path(
                      [
                        "PerformanceGroups",
                        performanceGroupIndex,
                        "BasketPackagesAndExtras",
                        basketItemIndex,
                        "OptionalExtrasGroups",
                      ],
                      basketData,
                    );

                    optExtrasGroup?.forEach((group) => {
                      const optExtraFoundIdx = group.OptionalExtras.findIndex(
                        (it) => it.Items[0].ItemId === opt.ItemId,
                      );

                      newOptExtrasGroup.push(
                        R.modifyPath(
                          [
                            "OptionalExtras",
                            optExtraFoundIdx,
                            "Items",
                            0,
                            "BasketQuantity",
                          ],
                          () =>
                            group.OptionalExtras[optExtraFoundIdx].Items[0]
                              .BasketQuantity - 1,
                          group,
                        ),
                      );
                    });

                    updateBasketProduct(
                      R.modifyPath(
                        [
                          "PerformanceGroups",
                          performanceGroupIndex,
                          "BasketPackagesAndExtras",
                          basketItemIndex,
                          "OptionalExtrasGroups",
                        ],
                        // @ts-expect-error
                        () => newOptExtrasGroup,
                        basketData,
                      ),
                    );
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      fill={
                        opt.BasketQuantity === 1 ? "transparent" : "#4FE8B5"
                      }
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      stroke={opt.BasketQuantity === 1 ? "#D7D7D7" : "#4FE8B5"}
                    />
                    <path
                      d="M22.1758 20.3242H14V19.1758H22.1758V20.3242Z"
                      fill={opt.BasketQuantity === 1 ? "#D7D7D7" : "#242425"}
                    />
                  </svg>
                </button>
                <div>{opt.BasketQuantity}</div>
                <button
                  onClick={() => {
                    // @ts-ignore
                    let newOptExtrasGroup = [];

                    const optExtrasGroup = R.path(
                      [
                        "PerformanceGroups",
                        performanceGroupIndex,
                        "BasketPackagesAndExtras",
                        basketItemIndex,
                        "OptionalExtrasGroups",
                      ],
                      basketData,
                    );

                    optExtrasGroup?.forEach((group) => {
                      const optExtraFoundIdx = group.OptionalExtras.findIndex(
                        (it) => it.Items[0].ItemId === opt.ItemId,
                      );

                      newOptExtrasGroup.push(
                        R.modifyPath(
                          [
                            "OptionalExtras",
                            optExtraFoundIdx,
                            "Items",
                            0,
                            "BasketQuantity",
                          ],
                          () =>
                            group.OptionalExtras[optExtraFoundIdx].Items[0]
                              .BasketQuantity + 1,
                          group,
                        ),
                      );
                    });

                    updateBasketProduct(
                      R.modifyPath(
                        [
                          "PerformanceGroups",
                          performanceGroupIndex,
                          "BasketPackagesAndExtras",
                          basketItemIndex,
                          "OptionalExtrasGroups",
                        ],
                        // @ts-expect-error
                        () => newOptExtrasGroup,
                        basketData,
                      ),
                    );
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      fill="#4FE8B5"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      stroke="#4FE8B5"
                    />
                    <path
                      d="M24.9639 20.3223H20.7139V24.5723H19.2861V20.3223H15.0361V18.9277H19.2861V14.6777H20.7139V18.9277H24.9639V20.3223Z"
                      fill="#242425"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="o-basket__package__info o-basket__package__info--mobile">
            <span>{labels.TotalText}</span>
            <span>{renderPrice(opt.PriceWithVat * opt.BasketQuantity)}</span>
          </div>
          <div className="o-basket__divider hide-mobile"></div>
          <div className="o-basket__package__remove-btn">
            <button
              onClick={() => setIsConfirmOptExtraOpen(opt.ItemId)}
              className="a-button remove-btn"
              title={labels.RemoveButtonText}
              aria-label={labels.RemoveButtonText}
            >
              <i className="zmdi zmdi-delete" style={{ fontSize: "24px" }}></i>
            </button>
          </div>
        </div>
      ))}
      {basketPackage.OptionalExtrasGroups?.length ? (
        <div className="o-basket__optional-extras">
          <div
            style={{
              display: "flex",
              flexDirection: isTablet ? "column" : "row",
              alignItems: isTablet ? "flex-start" : "center",
              gap: "16px",
              position: "relative",
            }}
          >
            {isTablet ? (
              <>
                <h5 style={{ flexGrow: 1, fontSize: "18px" }}>
                  {labels.OptionalExtraText}
                </h5>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "18px",
                    flexWrap: "wrap",
                  }}
                >
                  {basketPackage.OptionalExtrasGroups.map((group) =>
                    group.OptionalExtras.map((optionalExtra) => (
                      <img
                        alt={optionalExtra.ProductName}
                        key={optionalExtra.ProductId}
                        src={
                          optionalExtra.SmallImageUrl?.trim()
                            ? optionalExtra.SmallImageUrl
                            : labels.OptionalExtrasDefaultImage
                        }
                        height="40px"
                        width="40px"
                        style={{ borderRadius: "50%", marginRight: "-30px" }}
                      />
                    )),
                  )}
                </div>
                <button
                  style={{
                    outline: 0,
                    border: 0,
                    padding: 0,
                    background: "none",
                    height: "40px",
                    width: "40px",
                    transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                    position: "absolute",
                    top: "25px",
                    right: "16px",
                  }}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      fill="#4FE8B5"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      stroke="#4FE8B5"
                    />
                    <path
                      d="M20 16.8027L24.25 21.0527L23.2539 22.0488L20 18.7949L16.7461 22.0488L15.75 21.0527L20 16.8027Z"
                      fill="#242425"
                    />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <h5 style={{ flexGrow: 1 }}>{labels.OptionalExtraText}</h5>
                <span>
                  {labels.FromText + " " + renderPrice(optionalExtraMinPrice)}
                </span>
                <div style={{ paddingRight: "10px" }}>
                  {basketPackage.OptionalExtrasGroups.map((group) =>
                    group.OptionalExtras.map((optionalExtra) => (
                      <img
                        alt={optionalExtra.ProductName}
                        key={optionalExtra.ProductId}
                        src={
                          optionalExtra.SmallImageUrl?.trim()
                            ? optionalExtra.SmallImageUrl
                            : labels.OptionalExtrasDefaultImage
                        }
                        height="40px"
                        width="40px"
                        style={{ borderRadius: "50%", marginRight: "-10px" }}
                      />
                    )),
                  )}
                </div>
                <button
                  style={{
                    outline: 0,
                    border: 0,
                    padding: 0,
                    background: "none",
                    height: "40px",
                    width: "40px",
                    transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      fill="#4FE8B5"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="39"
                      height="39"
                      rx="19.5"
                      stroke="#4FE8B5"
                    />
                    <path
                      d="M20 16.8027L24.25 21.0527L23.2539 22.0488L20 18.7949L16.7461 22.0488L15.75 21.0527L20 16.8027Z"
                      fill="#242425"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
          {isExpanded && (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginTop: "20px",
                }}
              >
                <button
                  onClick={() => setActiveOptionalExtraGroup(null)}
                  style={{
                    outline: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "7px 15px",
                    color: "#242425",
                    border: "1px solid #242425",
                    borderRadius: "20px",
                    background: "transparent",
                    fontSize: "11px",
                    lineHeight: "140%",

                    ...(!Boolean(activeOptionalExtraGroup)
                      ? {
                          background: "#242425",
                          color: "#4FE8B5",
                        }
                      : {}),
                  }}
                >
                  View all
                </button>
                {basketPackage.OptionalExtrasGroups?.map(
                  (optionalExtraGroup) => (
                    <button
                      onClick={() =>
                        setActiveOptionalExtraGroup(optionalExtraGroup)
                      }
                      style={{
                        outline: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "7px 15px",
                        color: "#242425",
                        border: "1px solid #242425",
                        borderRadius: "20px",
                        background: "transparent",
                        fontSize: "11px",
                        lineHeight: "140%",

                        ...(activeOptionalExtraGroup?.GroupName ===
                        optionalExtraGroup.GroupName
                          ? {
                              background: "#242425",
                              color: "#4FE8B5",
                            }
                          : {}),
                      }}
                    >
                      {optionalExtraGroup.GroupName}
                    </button>
                  ),
                )}
              </div>
              <div
                style={{
                  marginTop: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {optionalExtraGroupToRender?.map((group, groupIndex) => (
                  <Fragment key={group.GroupName}>
                    <span>
                      {group.GroupName + ` (${group.OptionalExtras.length})`}
                    </span>
                    <div
                      style={{
                        marginTop: isTablet ? 0 : "16px",
                        display: "grid",
                        gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      {group.OptionalExtras.map((extra, extraIndex) => (
                        <OptionalExtraCount
                          labels={labels}
                          extra={extra}
                          key={extraIndex}
                          extraIndex={extraIndex}
                          renderPrice={renderPrice}
                          groupIndex={groupIndex}
                          basketData={basketData}
                          basketItemIndex={basketItemIndex}
                          performanceGroupIndex={performanceGroupIndex}
                          updateBasketProduct={updateBasketProduct}
                        />
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <></>
      )}
      <ConfirmModal
        isOpen={Boolean(isConfirmOpen)}
        onClose={() => setIsConfirmOpen(null)}
        onConfirm={() => {
          isConfirmOpen && removeBasketProduct();
          setIsConfirmOpen(null);
        }}
        title={labels.WarningModalTitle}
        message={labels.RemoveItemQuestionText}
        confirmText={labels.RemoveButtonText}
        cancelText={labels.RemoveItemCancelText}
      />
      <ConfirmModal
        isOpen={Boolean(isConfirmOptExtraOpen)}
        onClose={() => setIsConfirmOptExtraOpen(null)}
        onConfirm={() => {
          isConfirmOptExtraOpen && removeOptExtraProduct();
          setIsConfirmOptExtraOpen(null);
        }}
        title={labels.WarningModalTitle}
        message={labels.RemoveItemQuestionText}
        confirmText={labels.RemoveButtonText}
        cancelText={labels.RemoveItemCancelText}
      />
    </>
  );
}

export default BasketItem;
